/* eslint-disable no-param-reassign */
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { ITextRun } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { IEditorProps } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import { ITextEditor } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/text-editor.interface';
import { Container } from '@tencent/alloy-renderer';
import { IDisposable } from '@tencent/alloy-renderer/dist/lib/event-emitter';
import ua from '@tencent/docs-user-agent';
import _pick from 'lodash/pick';
import { CANVAS_WORD_ART_COMPONENT_ID } from '../../../id-constants';
import { InterfaceFactoryOptions } from '../../elements/factory-options.interface';
import { InterfaceRendererShapeComponent } from '../../elements/types';
import { checkShouldRenderWordArt } from './utils';
import { WordArtEventArea } from './word-art-event-area';
import { WordArtGraphics } from './word-art-graphics';
import { RenderExtraContext } from './word-art-type/common.interface';

export class WordArtComponent extends Container {
    // 图形构造入参
    private factoryOptions: InterfaceFactoryOptions<IShapeModel>;
    // 基础 graphics 部分
    private parentComponent: InterfaceRendererShapeComponent;
    // wordArt 图形
    private wordArtGraphics: WordArtGraphics | null;
    // wordArt 事件区域
    private wordArtEventArea: WordArtEventArea | null;

    private editorProps?: IEditorProps;
    private renderExtraContext: RenderExtraContext = {
        key: '',
        rectTextList: [],
        bgList: [],
    };
    private clickDisposer?: IDisposable;

    public constructor(
        factoryOptions: InterfaceFactoryOptions<IShapeModel>,
        parentComponent: InterfaceRendererShapeComponent,
    ) {
        super();
        this.id = `${CANVAS_WORD_ART_COMPONENT_ID}-${factoryOptions.model.key}--${factoryOptions.region}`;
        this.renderExtraContext.key = this.id;
        this.factoryOptions = factoryOptions;
        this.parentComponent = parentComponent;
        this.initWordArtEventArea();
        this.initWordArtGraphics();
        this.parentComponent.addChild(this);
        this.bindEvent();
    }

    public dispose = () => {
        super.dispose();
        this.clickDisposer?.dispose();
    };

    // 渲染组件预处理
    public renderComponentPreprocess = ({ editorProps }: { editorProps?: IEditorProps }) => {
        this.editorProps = editorProps;
        this.prepareParagraphList();
    };

    // 渲染组件
    public renderComponent = () => {
        // 渲染前先自检一下
        if (!checkShouldRenderWordArt(this.factoryOptions)) {
            this.hideSelfAndShowTextEditor();
            return;
        }

        if (!this.textEditor) return;

        this?.updateEager();

        const { children } = this.textEditor.editorContainer;

        children.forEach((child: any) => {
            if (child.id === 'text') {
                this.renderExtraContext.rectTextList = child.children
                    ?.map((rectText: any) => {
                        if (rectText.children) return rectText.children
                            ?.map((rectText: any) => this.pickRectText(rectText));
                        return this.pickRectText(rectText);
                    })
                    .flat() || [];
            }
            if (child.id === 'background') {
                this.renderExtraContext.bgList = child.children
                    ?.map((svgGraphics: any) => this.pickBg(svgGraphics)) || [];
            }
        });

        if (this.renderExtraContext.rectTextList.length === 0) return;

        this.update(this.renderExtraContext);
    };

    public onEnterEditing = () => {
        this.hideSelfAndShowTextEditor();
    };

    public onExitEditing = () => {
        this.hideTextEditorAndShowSelf();
    };

    private bindEvent = () => {
        if (!ua.isMobile) this.clickDisposer = this.onClick(this.clickCallback);
    };

    private hideSelfAndShowTextEditor = () => {
        this.visible = false;
        this.setTextEditorVisible(true);
    };

    private hideTextEditorAndShowSelf = () => {
        this.visible = true;
        this.setTextEditorVisible(false);
    };

    private initWordArtEventArea = () => {
        if (!window.pray()?.isPlaying) {
            this.wordArtEventArea = new WordArtEventArea(this.factoryOptions, this.parentComponent);
            this.wordArtEventArea.cursor = 'move';
            this.addChild(this.wordArtEventArea);
        }
    };

    private initWordArtGraphics = () => {
        this.wordArtGraphics = new WordArtGraphics(this.factoryOptions, this.parentComponent);
        this.wordArtGraphics.cursor = window.pray()?.isPlaying ? 'default' : 'text';
        this.addChild(this.wordArtGraphics);
    };

    private updateEager = () => {
        this.hideTextEditorWhenNotSelected();
        this.hideTextEditorWhenDisplayConflict();
        this.changeChildrenCursor();
    };

    private update = (context: RenderExtraContext) => {
        this.wordArtGraphics?.update(context);
        this.wordArtEventArea?.update();
    };

    // 当未选中时隐藏文本编辑器
    private hideTextEditorWhenNotSelected = () => {
        const { model } = this.factoryOptions;

        // 隐藏文本编辑器，显示 wordArt
        if (!model.selected) {
            this.visible = true;
            this.setTextEditorVisible(false);
        }
    };

    // 当显示冲突时隐藏编辑器
    private hideTextEditorWhenDisplayConflict = () => {
        if (this.visible && this.textEditor?.editorContainer.visible) {
            this.textEditor.editorContainer.visible = false;
        }
    };

    // 改变子元素 cursor
    private changeChildrenCursor = () => {
        if (this.wordArtEventArea) {
            this.wordArtEventArea.cursor = this.textEditor?.readonly ? 'default' : 'move';
        }
        if (this.wordArtGraphics) {
            this.wordArtGraphics.cursor = this.textEditor?.readonly ? 'default' : 'text';
            if (this.factoryOptions.model.isPlaceholder) {
                this.wordArtGraphics.cursor = window.pray?.().isPlaying ? 'default' : 'text';
            }
        }
    };

    // 设置 textEditor 是否可见
    private setTextEditorVisible = (visible: boolean) => {
        if (this.textEditor) {
            this.textEditor.editorContainer.visible = visible;
        }
    };

    // 获取实时的 textEditor
    private get textEditor() {
        const textEditor = (this.parentComponent as any).textEditor as ITextEditor | null;
        return textEditor;
    }

    private pickRectText = (rectText: any) =>
        _pick(rectText, [
            'content',
            'fontStyle',
            'fontWeight',
            'fontSize',
            'fontFamily',
            'width',
            'height',
            'localBounds.x',
            'localBounds.y',
            'rectXOffset',
            'rectYOffset',
            'alpha',
            'fill.color',
            'stroke.color.hex',
            'strokeWidth',
            'strokeLineCap',
            'strokeLineJoin',
            'rectUnderline',
            'rectLineThrough',
        ]);

    private pickBg = (bg: any) => _pick(bg, ['width', 'height', 'localBounds.x', 'localBounds.y', 'fill.color']);

    private prepareParagraphList = () => {
        this.editorProps?.paragraphList?.forEach((paragraph) => {
            paragraph.textRuns = paragraph.textRuns
                ?.map((textRun) => {
                    if (textRun.type === 'field') return textRun;
                    if (textRun?.text?.length === 1) return textRun;
                    return textRun?.text?.split('').map(char => ({
                        ...textRun,
                        text: char,
                    })) as ITextRun;
                })
                .flat();
        });
    };

    private clickCallback = (e: any) => {
        if (!this.editorProps) return;
        // 处理 placeholder 点击事件
        if (this.factoryOptions.model.isPlaceholder) {
            if (this.wordArtGraphics) {
                this.editorProps.onClick?.(e);
            }
        }
    };
}
